import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';

// Components
// ---------------------
import { ClickboxMenu } from '../../../../components/clickbox-menu/clickbox-menu';
import { Disclaimer } from '../../../../components/disclaimer/disclaimer';
import { MenuHeaderbar } from '../../../../components/menu-headerbar/menu-headerbar';
import { TabbedClickboxMenu } from '../../../../components/tabbed-clickbox-menu/tabbed-clickbox-menu';
import { LoadingSpinner } from '../../../../components/loading-spinner/loading-spinner';

// API
// ---------------------
import { dmDocumentList } from '../../../../api/api';

//TODO: use something like markdown-it or react-markdown to convert this from the markdown file to HTML
const DisclaimerText = {
  __html: `<em>
  <p>Delayed post-trade transparency information for SIGMA X MTF and SIGMA X Europe MTF's order books (the <strong>"delayed market data"</strong>) is made available at the link below, free of charge, in accordance with the MiFID II and MiFIR (including as incorporated under UK law) provisions relating to the publication of delayed market data. No registration is required. The delayed market data is provided with a maximum delay of 15 minutes.</p>
  <p><strong>The delayed market data does not contain real-time pre-trade or post-trade transparency information, which is made available separately, free of charge, to Participants pursuant to the <ins>SIGMA X MTF and SIGMA X Europe MTF Market Data Policy</ins>. See the Information for Participants page for more information.</strong></p>
  <p>By downloading the delayed market data, you hereby confirm that you, and any company on whose behalf you are accessing the delayed market data, are solely utilising the delayed market data in accordance with MiFID II / MiFIR (including as incorporated under UK law) and shall not at any time utilise the delayed market data for commercial purposes.</p>
  <p>By downloading the delayed market data you expressly acknowledge and agree that the delayed market data is provided on an "as is" basis, at your sole risk. Goldman Sachs expressly disclaims any implied warranties of merchantability or fitness for a particular purpose, including any warranty for the use, or the results of the use, of the delayed market data with respect to their correctness, quality, accuracy, security, safety, completeness, reliability, performance, timeliness, continued availability or otherwise.</p>
  <p>Goldman Sachs has no liability, contingent or otherwise, for your use of the delayed market data, the correctness, quality, accuracy, security, completeness, reliability, timeliness or continued availability of the delayed market data, or for the failure of any connection or communication service to provide or maintain the delayed market data, or for any interruption or disruption of access to the delayed market data.</p>
  <p><strong>Note:</strong> Please refer to the SIGMA X and SIGMA X Europe - Ap MD Feed Spec for details on the field specifications.</p>
  </em>`,
};

export const getDelayedMarketData = async (params) => {
  return await dmDocumentList({}, params).then((res) => res.data.documents);
};

export const fetchData = async () => {
  const preTradeEU = await getDelayedMarketData({
    feedId: 1622,
    documentTypeTag: 'PreTradeEU',
    count: 6,
  });
  const postTradeEU = await getDelayedMarketData({
    feedId: 1622,
    documentTypeTag: 'PostTradeEU',
    count: 6,
  });
  const preTradeUK = await getDelayedMarketData({
    feedId: 1622,
    documentTypeTag: 'PreTradeUK',
    count: 6,
  });
  const postTradeUK = await getDelayedMarketData({
    feedId: 1622,
    documentTypeTag: 'PostTradeUK',
    count: 6,
  });

  return {
    preTradeEU: preTradeEU,
    postTradeEU: postTradeEU,
    preTradeUK: preTradeUK,
    postTradeUK: postTradeUK,
  };
};

/**
 * Determines if the document should be displayed under the "historical data" menu
 * @param {*} title Document Title
 * @returns true or false
 */
export const filterHistoricalDataItems = (title, today) => {
  const regex = /[0-9]{4}-[0-9]{2}-[0-9]{2}/;
  const titleDate = new Date(title.match(regex)[0]);

  return titleDate < new Date(today);
};

export const mapDocumentsToMenu = (documents) => {
  const documentInfo = {
    PostTradeEU: {
      name: 'Post Trade Transparency Data (EU)',
      path: 'TradePubEUDownload',
    },
    PreTradeEU: {
      name: 'Pre Trade Transparency Data (EU)',
      path: 'PreTradeEUQuoteDownload',
    },
    PostTradeUK: {
      name: 'Post Trade Transparency Data (UK)',
      path: 'TradePubUKDownload',
    },
    PreTradeUK: {
      name: 'Pre Trade Transparency Data (UK)',
      path: 'PreTradeUKQuoteDownload',
    },
  };
  return documents.map((document) => {
    const date = document.documentDate.split(' ')[0];
    return {
      item: `${
        documentInfo[document.documentTypeTag].name
      } Transparency Data ${date}`,
      path: `/sigmaxmtf/${documentInfo[document.documentTypeTag].path}?docTag=${
        document.documentTag
      }`,
      isDownload: true,
      newTab: true,
    };
  });
};

export const DelayedMarketData = ({ ...props }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [menuItems, setMenuItems] = useState([]);

  const today = moment().tz('Europe/London').format('YYYY-MM-DD');

  const menuTabs = [
    { label: 'Pre Trade Transparency', active: true },
    { label: 'Post Trade Transparency', active: false },
  ];

  useEffect(() => {
    fetchData()
      .then((response) => {
        const preTradeEU = response.preTradeEU
          ? mapDocumentsToMenu(response.preTradeEU)
          : [];
        const postTradeEU = response.postTradeEU
          ? mapDocumentsToMenu(response.postTradeEU)
          : [];
        const preTradeUK = response.preTradeUK
          ? mapDocumentsToMenu(response.preTradeUK)
          : [];
        const postTradeUK = response.postTradeUK
          ? mapDocumentsToMenu(response.postTradeUK)
          : [];

        setMenuItems([
          ...preTradeEU,
          ...postTradeEU,
          ...preTradeUK,
          ...postTradeUK,
        ]);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  const todaysData =
    menuItems.filter((item) => item.item.includes(today)) || [];
  const historicalData =
    menuItems.filter(
      (item) =>
        !item.item.includes(today) &&
        filterHistoricalDataItems(item.item, today)
    ) || [];
  const preTradeData = historicalData.filter((datum) =>
    datum.item.includes(menuTabs[0].label)
  );
  const postTradeData = historicalData.filter((datum) =>
    datum.item.includes(menuTabs[1].label)
  );

  return (
    <div id="delayed-market-data">
      <LoadingSpinner status={isLoading} name={'Delayed Market Data'} />
      <div hidden={isLoading}>
        <Disclaimer
          text="Delayed Market Data"
          disclaimerText={DisclaimerText}
        />
        <MenuHeaderbar text="Today's Data" />
        <ClickboxMenu
          menuItems={todaysData}
          aria="Today's Delayed Market Data Menu"
        />
        <MenuHeaderbar text="Historical Data" />
        <TabbedClickboxMenu
          menuTabs={menuTabs}
          tabsContent={[preTradeData, postTradeData]}
          aria="Historical Delayed Market Data Tablist"
        />
      </div>
    </div>
  );
};
